import {PoolStatisticsApiInterface} from "../api/interface/pool-statistics";
import type {PoolStatisticsMode} from "../api/payload-types/pool-statistics";
import {generateDefaultQueryError} from "../util/errorUtil";
import {selectCredentials} from "./authSlice";
import {
    clearPoolStatisticsCache,
    selectPoolStatisticsCache,
    selectPoolStatisticsInitialized,
    selectPoolStatisticsMode,
    setPoolStatisticsError,
    setPoolStatisticsLoaded,
    setPoolStatisticsLoading,
    setPoolStatisticsMode,
} from "./poolStatisticsSlice";
import type {RevaThunkAction} from "./revaHooks";

/**
 * #19113: Initialize the POOL tab's pool statistics on first activation. No-op if already
 * initialized (the panel/tab is mounted on demand, on first activation of the POOL tab). Loads the
 * currently selected mode (default WEEKS).
 */
export const initializePoolStatisticsThunk = (): RevaThunkAction<Promise<void>> => async (dispatch, getState) => {
    if (selectPoolStatisticsInitialized(getState())) {
        return;
    }
    await dispatch(loadPoolStatisticsThunk(selectPoolStatisticsMode(getState())));
};

/**
 * Load the current order-pool statistics snapshot for the requesting user in the given mode.
 * @param mode time unit of the threshold tiles and chart buckets (WEEKS / MONTHS / DYNAMIC)
 */
export const loadPoolStatisticsThunk =
    (mode: PoolStatisticsMode): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}) => {
        dispatch(setPoolStatisticsLoading());
        const credentials = selectCredentials(getState());
        try {
            const response = await new PoolStatisticsApiInterface(mode).execute(serviceApi, credentials!);
            dispatch(setPoolStatisticsLoaded({mode, data: response}));
        } catch (err) {
            dispatch(setPoolStatisticsError(generateDefaultQueryError(err as Error)));
        }
    };

/**
 * Switch the statistics to another time-unit mode; loads the data on first use of the mode
 * (afterwards it is served from the slice cache).
 * @param mode mode to switch to
 */
export const changePoolStatisticsModeThunk =
    (mode: PoolStatisticsMode): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        dispatch(setPoolStatisticsMode(mode));
        if (!selectPoolStatisticsCache(getState())[mode]) {
            await dispatch(loadPoolStatisticsThunk(mode));
        }
    };

/**
 * Reload the pool statistics (triggered by the panel's refresh button): the cache is dropped, so
 * all modes are fetched fresh on next use, and the current mode is reloaded immediately.
 */
export const reloadPoolStatisticsThunk = (): RevaThunkAction<Promise<void>> => async (dispatch, getState) => {
    dispatch(clearPoolStatisticsCache());
    await dispatch(loadPoolStatisticsThunk(selectPoolStatisticsMode(getState())));
};
